import { createContext, useContext } from 'react'
import { america, findMission, missionAt, missionPath } from '../content/missions.ts'
import { displayFonts, sansFonts } from '../content/fonts.ts'

import { markStyles } from '../content/marks.ts'

export { markStyles }
export const radiusStyles = ['round', 'soft', 'square'] as const
export const densityStyles = ['comfortable', 'compact'] as const
export const heroMedia = ['photo', 'video', 'flag', 'none'] as const
export const footerStyles = ['columns', 'editorial', 'dark', 'studio'] as const
export const footerIdentifiers = ['off', 'on'] as const
export const footerRules = ['line', 'double', 'stripes', 'none'] as const
export const socialStyles = ['icons', 'circles', 'none'] as const
export const footerHeadings = ['mono', 'sans', 'serif', 'small'] as const
export const footerLists = ['plain', 'bullets', 'arrows'] as const
export const navStyles = ['menu', 'hamburger', 'links'] as const
export const linkStyles = ['text', 'outline', 'solid'] as const
export const promptStyles = ['raised', 'outline', 'glass'] as const
export const creditStyles = ['caption', 'badge'] as const
export const eyebrowStyles = ['mono', 'sans', 'serif', 'accent'] as const
export const numberStyles = ['serif', 'sans', 'mono'] as const
export const ctaStyles = ['primary', 'accent', 'outline'] as const
export const wordmarkStyles = ['sans', 'sans-bold', 'serif', 'mono'] as const
export const wordmarkLayouts = ['full', 'stacked', 'initials'] as const
export const bannerModes = ['dialog', 'expand'] as const
export const bannerAligns = ['center', 'left'] as const
export const bannerScrolls = ['scroll', 'sticky'] as const
export const bannerTones = ['theme', 'dark', 'primary'] as const
export const sectionLinks = ['outline', 'solid', 'text'] as const
export const closingBackgrounds = ['gradient', 'dark', 'plain'] as const
export const casings = ['title', 'sentence'] as const
export const alertStyles = ['soft', 'solid', 'outline', 'bar'] as const
export const accordionStyles = ['lines', 'cards'] as const
export const accordionIcons = ['plus', 'chevron'] as const
export const textSizes = ['small', 'default', 'large'] as const
export const labelStyles = ['regular', 'bold', 'caps'] as const
export const fieldStyles = ['outline', 'filled', 'underline'] as const
export const buttonStyles = ['medium', 'bold', 'caps'] as const
export const brandNames = ['word', 'initials', 'name', 'mds'] as const

/** A custom palette: primary, secondary and accent hex colors, comma separated. */
export const palettePattern = /^#[0-9a-f]{6},#[0-9a-f]{6},#[0-9a-f]{6}$/

/** Motion durations in milliseconds: fast, base, slow and story, comma separated. */
export const durationsPattern = /^\d{1,4},\d{1,4},\d{1,4},\d{1,4}$/
export const durationTokens = ['fast', 'base', 'slow', 'story'] as const
export const defaultDurations = [150, 250, 500, 900]

export type Prefs = {
  theme: 'light' | 'dark'
  mission: string
  mark: (typeof markStyles)[number]
  fontDisplay: string
  fontSans: string
  radius: (typeof radiusStyles)[number]
  density: (typeof densityStyles)[number]
  hero: (typeof heroMedia)[number]
  footer: (typeof footerStyles)[number]
  footerIdentifier: (typeof footerIdentifiers)[number]
  footerRule: (typeof footerRules)[number]
  socialStyle: (typeof socialStyles)[number]
  footerHeading: (typeof footerHeadings)[number]
  footerList: (typeof footerLists)[number]
  nav: (typeof navStyles)[number]
  heroLinks: (typeof linkStyles)[number]
  prompt: (typeof promptStyles)[number]
  credit: (typeof creditStyles)[number]
  eyebrow: (typeof eyebrowStyles)[number]
  numbers: (typeof numberStyles)[number]
  cta: (typeof ctaStyles)[number]
  wordmark: (typeof wordmarkStyles)[number]
  wordmarkLayout: (typeof wordmarkLayouts)[number]
  bannerMode: (typeof bannerModes)[number]
  bannerAlign: (typeof bannerAligns)[number]
  bannerScroll: (typeof bannerScrolls)[number]
  bannerTone: (typeof bannerTones)[number]
  sectionLink: (typeof sectionLinks)[number]
  closing: (typeof closingBackgrounds)[number]
  casing: (typeof casings)[number]
  alert: (typeof alertStyles)[number]
  accordion: (typeof accordionStyles)[number]
  accordionIcon: (typeof accordionIcons)[number]
  textSize: (typeof textSizes)[number]
  label: (typeof labelStyles)[number]
  field: (typeof fieldStyles)[number]
  button: (typeof buttonStyles)[number]
  brand: (typeof brandNames)[number]
  /** Empty for the mission's own colors. */
  palette: string
  /** Empty for the default motion durations. */
  durations: string
  notes: boolean
}

export const defaultPrefs: Omit<Prefs, 'theme'> = {
  mission: 'america',
  mark: 'flag',
  fontDisplay: 'instrument-serif',
  fontSans: 'inter',
  radius: 'round',
  density: 'comfortable',
  hero: 'photo',
  footer: 'columns',
  footerIdentifier: 'off',
  footerRule: 'line',
  socialStyle: 'icons',
  footerHeading: 'mono',
  footerList: 'plain',
  nav: 'menu',
  heroLinks: 'text',
  prompt: 'raised',
  credit: 'caption',
  eyebrow: 'mono',
  numbers: 'serif',
  cta: 'primary',
  wordmark: 'sans',
  wordmarkLayout: 'full',
  bannerMode: 'dialog',
  bannerAlign: 'center',
  bannerScroll: 'scroll',
  bannerTone: 'theme',
  sectionLink: 'outline',
  closing: 'gradient',
  casing: 'title',
  alert: 'soft',
  accordion: 'lines',
  accordionIcon: 'plus',
  textSize: 'default',
  label: 'regular',
  field: 'outline',
  button: 'medium',
  brand: 'word',
  palette: '',
  durations: '',
  notes: true,
}

export const STORAGE_KEY = 'mds:prefs'
/** The roles derived from a custom palette, read by theme-init.js before paint. */
export const CUSTOM_ROLES_KEY = 'mds:custom-roles'

/** The attributes on <html> that each preference drives. */
export const attributes: Partial<Record<keyof Prefs, string>> = {
  theme: 'data-theme',
  mission: 'data-mission',
  mark: 'data-mark',
  fontDisplay: 'data-font-display',
  fontSans: 'data-font-sans',
  radius: 'data-radius',
  density: 'data-density',
  wordmark: 'data-wordmark',
  wordmarkLayout: 'data-wordmark-layout',
  nav: 'data-nav',
  eyebrow: 'data-eyebrow',
  numbers: 'data-numbers',
  alert: 'data-alert',
  accordion: 'data-accordion',
  accordionIcon: 'data-accordion-icon',
  textSize: 'data-text-size',
  label: 'data-label',
  field: 'data-field',
  button: 'data-button',
  bannerScroll: 'data-banner-scroll',
  bannerTone: 'data-banner-tone',
  footerHeading: 'data-footer-heading',
  footerList: 'data-footer-list',
}

export function systemTheme(): Prefs['theme'] {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  } catch {
    return 'light'
  }
}

/** Allowed values for each enumerated preference; anything else is dropped. */
const allowed: Partial<Record<keyof Prefs, readonly string[]>> = {
  theme: ['light', 'dark'],
  mark: markStyles,
  radius: radiusStyles,
  density: densityStyles,
  hero: heroMedia,
  footer: footerStyles,
  footerIdentifier: footerIdentifiers,
  footerRule: footerRules,
  socialStyle: socialStyles,
  footerHeading: footerHeadings,
  footerList: footerLists,
  nav: navStyles,
  heroLinks: linkStyles,
  prompt: promptStyles,
  credit: creditStyles,
  eyebrow: eyebrowStyles,
  numbers: numberStyles,
  cta: ctaStyles,
  wordmark: wordmarkStyles,
  wordmarkLayout: wordmarkLayouts,
  bannerMode: bannerModes,
  bannerAlign: bannerAligns,
  bannerScroll: bannerScrolls,
  bannerTone: bannerTones,
  sectionLink: sectionLinks,
  closing: closingBackgrounds,
  casing: casings,
  alert: alertStyles,
  accordion: accordionStyles,
  accordionIcon: accordionIcons,
  textSize: textSizes,
  label: labelStyles,
  field: fieldStyles,
  button: buttonStyles,
  brand: brandNames,
}

/**
 * Keeps only real preferences with values of the right kind: known keys, the
 * default's type, the allowed values where a list exists, and short plain
 * strings elsewhere. Stored choices and shared links both pass through it.
 */
export function sanitizePrefs(value: unknown): Partial<Prefs> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const clean: Record<string, unknown> = {}
  const defaults: Record<string, unknown> = { ...defaultPrefs, theme: 'light' }
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (!Object.hasOwn(defaults, key)) continue
    if (typeof entry !== typeof defaults[key]) continue
    if (typeof entry === 'string' && !/^[a-z0-9#,.-]{0,64}$/i.test(entry)) continue
    const options = allowed[key as keyof Prefs]
    if (options && !options.includes(entry as string)) continue
    if (key === 'palette' && entry !== '' && !palettePattern.test(entry as string)) continue
    if (key === 'durations' && entry !== '' && !durationsPattern.test(entry as string)) continue
    clean[key] = entry
  }
  return clean as Partial<Prefs>
}

export function readStored(): Partial<Prefs> {
  try {
    return sanitizePrefs(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'))
  } catch {
    return {}
  }
}

/** The address hash that carries a shared design. */
/** The hash a shared design travels in. */
export const SHARE_PREFIX = '#d='
/** Links shared before the compact codes: the design as base64 JSON. */
const LEGACY_PREFIX = '#design='

/** Whether a hash carries a shared design, in either form. */
export const isSharedHash = (hash: string) => hash.startsWith(SHARE_PREFIX) || hash.startsWith(LEGACY_PREFIX)

/**
 * One letter per choice in a shared link. A link names only the choices that
 * differ from the defaults, each as its letter and the position of the option
 * in its list, so one change is two characters. Letters and the order of each
 * option list are fixed once published: a new choice takes a new letter and a
 * new option goes at the end of its list, so every link ever shared still
 * reads the same.
 */
export const shareCodes: ReadonlyArray<readonly [string, keyof Prefs]> = [
  ['t', 'theme'], ['m', 'mission'], ['k', 'mark'], ['d', 'fontDisplay'], ['s', 'fontSans'],
  ['r', 'radius'], ['y', 'density'], ['h', 'hero'], ['f', 'footer'], ['i', 'footerIdentifier'],
  ['l', 'footerRule'], ['o', 'socialStyle'], ['g', 'footerHeading'], ['j', 'footerList'], ['n', 'nav'],
  ['x', 'heroLinks'], ['q', 'prompt'], ['c', 'credit'], ['e', 'eyebrow'], ['b', 'numbers'],
  ['a', 'cta'], ['w', 'wordmark'], ['v', 'wordmarkLayout'], ['B', 'bannerMode'], ['A', 'bannerAlign'],
  ['S', 'bannerScroll'], ['T', 'bannerTone'], ['L', 'sectionLink'], ['C', 'closing'], ['K', 'casing'],
  ['E', 'alert'], ['O', 'accordion'], ['I', 'accordionIcon'], ['X', 'textSize'], ['N', 'label'],
  ['F', 'field'], ['U', 'button'], ['R', 'brand'], ['p', 'palette'], ['u', 'durations'],
]

/** The options a choice's code indexes, for every choice but the free-form ones. */
export function shareOptions(key: keyof Prefs): readonly string[] | undefined {
  if (key === 'fontDisplay') return displayFonts.map((font) => font.id)
  if (key === 'fontSans') return sansFonts.map((font) => font.id)
  return allowed[key]
}

/** A design's choices as a compact code, such as `r2` for square corners. */
export function encodeDesign(design: Partial<Prefs>): string {
  let code = ''
  for (const [letter, key] of shareCodes) {
    const value = design[key]
    if (value === undefined || value === '') continue
    if (key === 'palette') code += letter + String(value).replace(/[#,]/g, '')
    else if (key === 'durations') code += `${letter}${String(value).replace(/,/g, '-')}.`
    else if (key === 'mission') code += `${letter}${value}.`
    else {
      const index = shareOptions(key)?.indexOf(String(value)) ?? -1
      if (index >= 0) code += letter + index.toString(36)
    }
  }
  return code
}

/** The choices in a compact code, checked like stored choices. */
export function decodeDesign(code: string): Partial<Prefs> {
  const letters = new Map(shareCodes)
  const design: Record<string, string> = {}
  let at = 0
  while (at < code.length) {
    const key = letters.get(code[at])
    at += 1
    if (!key) break
    if (key === 'palette') {
      const hex = code.slice(at, at + 18)
      at += 18
      design.palette = [0, 6, 12].map((start) => `#${hex.slice(start, start + 6)}`).join(',')
    } else if (key === 'mission' || key === 'durations') {
      const stop = code.indexOf('.', at)
      const value = code.slice(at, stop < 0 ? code.length : stop)
      at = stop < 0 ? code.length : stop + 1
      design[key] = key === 'durations' ? value.replace(/-/g, ',') : value
    } else {
      const option = shareOptions(key)?.[parseInt(code[at], 36)]
      at += 1
      if (option) design[key] = option
    }
  }
  return sanitizePrefs(design)
}

/**
 * A link that rebuilds the current design. It names only the choices that
 * differ from the defaults. Light or dark is each visitor's own setting, not
 * part of the design, so a link never carries it; links that did still open. On
 * the home page the mission is the address itself, /cbp, so a mission with no
 * other changes is just that address. Design mode is how a visitor works, not
 * part of the design, so it stays out.
 */
export function shareUrl(
  prefs: Prefs,
  { pathname = window.location.pathname, origin = window.location.origin } = {},
) {
  const changed: Partial<Prefs> = Object.fromEntries(
    Object.entries(prefs).filter(
      ([key, value]) =>
        key !== 'notes' && key !== 'theme' && value !== (defaultPrefs as Record<string, unknown>)[key],
    ),
  )
  const home = missionAt(pathname)
  if (home) delete changed.mission
  const path = home ? missionPath(findMission(prefs.mission) ?? america) : pathname
  const code = encodeDesign(changed)
  return code ? `${origin}${path}${SHARE_PREFIX}${code}` : `${origin}${path}`
}

/** The design a shared link carries, checked like stored choices, or null. */
export function readShared(hash: string): Partial<Prefs> | null {
  if (hash.startsWith(SHARE_PREFIX)) return decodeDesign(hash.slice(SHARE_PREFIX.length))
  if (!hash.startsWith(LEGACY_PREFIX)) return null
  try {
    const encoded = hash.slice(LEGACY_PREFIX.length).replace(/-/g, '+').replace(/_/g, '/')
    return sanitizePrefs(JSON.parse(atob(encoded)))
  } catch {
    return null
  }
}

export function writeStored(value: Partial<Prefs>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Private windows and blocked storage still get a working session.
  }
}

export type PrefsContext = {
  prefs: Prefs
  /** True once the visitor has picked a theme; until then it follows the OS. */
  themeChosen: boolean
  set: <K extends keyof Prefs>(key: K, value: Prefs[K]) => void
  reset: () => void
}

export const PrefsContextValue = createContext<PrefsContext | null>(null)

export function usePrefs() {
  const context = useContext(PrefsContextValue)
  if (!context) throw new Error('usePrefs must be used inside PrefsProvider')
  return context
}
